import {
  createEmptyInputState,
  createEmptySnapshot,
  FISHYMAP_BRIDGE_V2_VERSION,
  FISHYMAP_CONTRACT_VERSION,
  FISHYMAP_EVENTS,
} from "./map-bridge-contract.js";
import {
  assertFishyMapHotEventPayload,
  estimateJsonByteLength,
  summarizeBridgeEventBatch,
  validateFishyMapHotEventPayload,
} from "./map-bridge-payload.js";
import {
  applyStatePatch,
  mergeStatePatch,
  normalizeStatePatch,
  patchHasCommands,
  patchHasStateFields,
} from "./map-bridge-input.js";
import {
  applyV2InputAcknowledgement,
  applyV2StateRevisions,
  assertFishyMapV2EventBatch,
  projectFishyMapSnapshotPayload,
  projectFishyMapStateEvent,
  resolvedCurrentViewMode,
} from "./map-bridge-output.js";
import {
  commandsToFishyMapV2CommandEnvelopes,
  createFishyMapV2InputPatchBatch,
  normalizeFishyMapV2SnapshotKind,
  normalizeFishyMapV2SnapshotRequest,
  statePatchToFishyMapV2Patches,
} from "./map-bridge-v2-input.js";
import {
  createHostRafMetrics,
  hostRafCounterSnapshot,
  mainThreadPressureCounterSnapshot,
  summarizeHostRafMetrics,
  summarizeMainThreadPressure,
} from "./map-main-thread-pressure.js";
import {
  addPerformanceCounter,
  addPerformanceGauge,
  addPerformanceSpanSample,
  createPerfSentinel,
  createPerformanceCollector,
  emptyProfileSummary,
  emptyQuantileSummary,
  normalizeErrorRecord,
  normalizeLoafEntry,
  normalizePerfCounterSuffix,
  normalizePerformanceOptions,
  performanceNowMs,
  pushRing,
  recordFilterPerfMetrics,
  setPerformanceLast,
  snapshotPerformanceCollector,
} from "./map-performance-collector.js";
import {
  loadMapRuntimeModule,
  notifyMapRuntimeLoadProgress,
} from "./map-runtime-loader.js";
import {
  collectMapRuntimeResourceDiscovery,
  detectWasmThreadingReadiness,
  parseBooleanParam,
  resolveMapRuntimeMemoryBudgetMiB,
} from "./map-runtime-settings.js";
import { extractThemeSnapshot } from "./map-theme-state.js";

const THEME_EVENT = "fishystuff:themechange";
const HOST_V2_SHELL_DRAIN_BUDGET_MS = 8;
const HOST_V2_LISTENER_BUDGET_MS = 8;
const FISHY_PERF_QUERY_PARAM = "fishy_perf";

function scheduleBrowserTask(callback) {
  const task = { cancelled: false, channel: null };
  const MessageChannelImpl = globalThis.window?.MessageChannel || globalThis.MessageChannel;
  if (typeof MessageChannelImpl === "function") {
    const channel = new MessageChannelImpl();
    task.channel = channel;
    channel.port1.onmessage = () => {
      channel.port1.close?.();
      channel.port2.close?.();
      task.channel = null;
      if (!task.cancelled) {
        callback();
      }
    };
    channel.port2.postMessage(undefined);
    return task;
  }

  Promise.resolve().then(() => {
    if (!task.cancelled) {
      callback();
    }
  });
  return task;
}

function scheduleBrowserMicrotask(callback) {
  const task = { cancelled: false };
  const run = () => {
    if (!task.cancelled) {
      callback();
    }
  };
  if (typeof globalThis.queueMicrotask === "function") {
    globalThis.queueMicrotask(run);
  } else {
    Promise.resolve().then(run);
  }
  return task;
}

function browserAnimationFrameScheduler() {
  const requestAnimationFrameRoot =
    typeof globalThis.window?.requestAnimationFrame === "function"
      ? globalThis.window
      : globalThis;
  const requestAnimationFrameImpl = requestAnimationFrameRoot.requestAnimationFrame;
  if (
    typeof requestAnimationFrameImpl !== "function" ||
    globalThis.document?.visibilityState === "hidden"
  ) {
    return null;
  }
  return (callback) => requestAnimationFrameImpl.call(requestAnimationFrameRoot, callback);
}

async function yieldToBrowserMainThread() {
  const browserScheduler = globalThis.window?.scheduler;
  const schedulerYield =
    browserScheduler && browserScheduler === globalThis.scheduler
      ? browserScheduler.yield
      : undefined;
  if (typeof schedulerYield === "function") {
    await schedulerYield.call(browserScheduler);
    return;
  }
  await new Promise((resolve) => {
    scheduleBrowserTask(resolve);
  });
}

async function yieldForBrowserPaint({ frames = 1 } = {}) {
  const frameCount = Math.max(1, Math.min(4, Number.parseInt(frames, 10) || 1));
  const scheduleAnimationFrame = browserAnimationFrameScheduler();
  if (!scheduleAnimationFrame) {
    await new Promise((resolve) => {
      scheduleBrowserTask(resolve);
    });
    return;
  }
  await new Promise((resolve) => {
    const step = (remainingFrames) => {
      if (remainingFrames <= 0) {
        resolve();
        return;
      }
      scheduleAnimationFrame(() => step(remainingFrames - 1));
    };
    step(frameCount);
  });
}

function cancelBrowserTask(task) {
  if (!task) {
    return;
  }
  task.cancelled = true;
  task.channel?.port1?.close?.();
  task.channel?.port2?.close?.();
  task.channel = null;
}

function cloneJson(value) {
  if (value == null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(object, key) {
  return !!object && Object.prototype.hasOwnProperty.call(object, key);
}

function isEditableKeyboardTarget(target) {
  if (!target || target === globalThis.document || target === globalThis.window) {
    return false;
  }
  const tagName = String(target.tagName || "").toLowerCase();
  return Boolean(
    target.isContentEditable ||
      tagName === "input" ||
      tagName === "textarea" ||
      tagName === "select",
  );
}

export function shouldSuppressMapNavigationCloseShortcut(event, state, canvas) {
  if (!event || !canvas) {
    return false;
  }
  const isCtrlW =
    event.ctrlKey === true &&
    event.metaKey !== true &&
    event.altKey !== true &&
    (event.code === "KeyW" || String(event.key || "").toLowerCase() === "w");
  if (!isCtrlW || state?.view?.viewMode !== "3d") {
    return false;
  }
  if (isEditableKeyboardTarget(event.target)) {
    return false;
  }
  return globalThis.document?.activeElement === canvas;
}

function patchWithoutCommands(patch) {
  const normalized = normalizeStatePatch(patch);
  delete normalized.commands;
  return normalized;
}

function createCustomEvent(type, detail) {
  if (typeof CustomEvent === "function") {
    return new CustomEvent(type, { detail });
  }
  const event = new Event(type);
  event.detail = detail;
  return event;
}

function buildThemePatch(env) {
  const theme = extractThemeSnapshot(env);
  if (!theme.name && !Object.keys(theme.colors || {}).length) {
    return undefined;
  }
  return normalizeStatePatch({
    version: FISHYMAP_CONTRACT_VERSION,
    theme,
  });
}

function fishyMapPerfEnabled(locationHref = globalThis.location?.href) {
  try {
    const params = new URL(locationHref || "", "https://fishystuff.fish").searchParams;
    return parseBooleanParam(params.get(FISHY_PERF_QUERY_PARAM)) === true;
  } catch (_) {
    return false;
  }
}

function isMeaningfulPatch(patch) {
  const normalized = normalizeStatePatch(patch);
  return patchHasStateFields(normalized) || patchHasCommands(normalized);
}

function buildStartupStatePatch(memoryBudgetMiB, initialState) {
  return mergeStatePatch(
    {
      settings: { memoryBudgetMiB },
    },
    initialState,
  );
}

function assertBridgeV2Exports(wasmModule) {
  const required = [
    "fishymap_v2_apply_patch",
    "fishymap_v2_send_command",
    "fishymap_v2_request_snapshot",
    "fishymap_v2_get_telemetry",
    "fishymap_v2_set_event_sink",
    "fishymap_v2_clear_event_sink",
  ];
  const missing = required.filter((name) => typeof wasmModule?.[name] !== "function");
  if (missing.length) {
    throw new Error(`map runtime missing Bridge v2 exports: ${missing.join(", ")}`);
  }
}

class FishyMapBridgeImpl {
  constructor() {
    this.container = null;
    this.canvas = null;
    this.wasmModule = null;
    this.wasmReady = false;
    this.runtimeInputReady = false;
    this.runtimeInputReleasePending = false;
    this.rendererStarted = false;
    this.runtimePreparation = null;
    this.firstFrameReady = false;
    this.loadProgressCallback = null;
    this.inputState = createEmptyInputState();
    this.currentState = createEmptySnapshot();
    this.runtimeSettings = {
      wasmThreadCount: 0,
      memoryBudgetMiB: 256,
    };
    this.resourceDiscovery = null;
    this.pendingStatePatch = normalizeStatePatch({});
    this.pendingCommands = [];
    this.v2InputSequence = 0;
    this.v2CommandSequence = 0;
    this.v2OutputSequence = 0;
    this.appliedInputSequence = 0;
    this.stateRevisions = {};
    this.deferredStateRefreshKind = "";
    this.pendingShellV2Events = [];
    this.pendingShellAppliedInputSequence = 0;
    this.postSinkDrainTask = null;
    this.drainingPostSink = false;
    this.rafMetrics = createHostRafMetrics();
    this.rafMetricsTimer = 0;
    this.resizeObserver = null;
    this.themeObserver = null;
    this.boundEventSink = (batch) => this.handleWasmV2EventBatch(batch);
    this.boundThemeSync = () => {
      this.syncTheme();
    };
    this.boundResize = () => {
      this.syncCanvasSize();
    };
    this.boundCanvasContextMenu = (event) => {
      event?.preventDefault?.();
    };
    this.boundCanvasPointerDown = () => {
      if (typeof this.canvas?.focus === "function") {
        this.canvas.focus({ preventScroll: true });
      }
    };
    this.boundDocumentKeyDown = (event) => {
      if (!shouldSuppressMapNavigationCloseShortcut(event, this.currentState, this.canvas)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.addPerformanceCounter("host.hotkeys.ctrl_w_suppressed");
    };
    this.performanceOptions = normalizePerformanceOptions({
      scenario: "load_map",
      captureSpans: false,
      captureFrames: false,
    });
    this.performanceCollector = createPerformanceCollector(this.performanceOptions.scenario);
    this.perfSentinel = createPerfSentinel(fishyMapPerfEnabled());
    this.boundPerfError = (event) => {
      this.recordRecentError(event?.error || event?.message || "window error", {
        source: "window.error",
        type: event?.type,
      });
    };
    this.boundPerfUnhandledRejection = (event) => {
      this.recordRecentError(event?.reason || "unhandled rejection", {
        source: "window.unhandledrejection",
        type: event?.type,
      });
    };
  }

  measurePerformanceSpan(name, callback) {
    const startedAtMs = performanceNowMs();
    try {
      return callback();
    } finally {
      addPerformanceSpanSample(this.performanceCollector, name, performanceNowMs() - startedAtMs);
    }
  }

  async measurePerformanceSpanAsync(name, callback) {
    const startedAtMs = performanceNowMs();
    try {
      return await callback();
    } finally {
      addPerformanceSpanSample(this.performanceCollector, name, performanceNowMs() - startedAtMs);
    }
  }

  async yieldToBrowser(stage = "unknown") {
    const suffix = normalizePerfCounterSuffix(stage) || "unknown";
    const startedAtMs = performanceNowMs();
    try {
      await yieldToBrowserMainThread();
    } finally {
      addPerformanceSpanSample(
        this.performanceCollector,
        `host.yield.${suffix}`,
        performanceNowMs() - startedAtMs,
      );
      this.addPerformanceCounter(`host.yields.${suffix}`);
    }
  }

  observeBrowserYield(stage = "unknown") {
    const suffix = normalizePerfCounterSuffix(stage) || "unknown";
    const startedAtMs = performanceNowMs();
    this.addPerformanceCounter(`host.yields_async.${suffix}.scheduled`);
    void yieldToBrowserMainThread()
      .then(() => {
        addPerformanceSpanSample(
          this.performanceCollector,
          `host.yield_async.${suffix}`,
          performanceNowMs() - startedAtMs,
        );
        this.addPerformanceCounter(`host.yields_async.${suffix}`);
      })
      .catch((error) => {
        this.recordRecentError(error, { source: `host.yield_async.${suffix}` });
      });
  }

  async yieldToBrowserPaint(stage = "unknown", options = {}) {
    const suffix = normalizePerfCounterSuffix(stage) || "unknown";
    const startedAtMs = performanceNowMs();
    try {
      await yieldForBrowserPaint(options);
    } finally {
      addPerformanceSpanSample(
        this.performanceCollector,
        `host.paint_yield.${suffix}`,
        performanceNowMs() - startedAtMs,
      );
      this.addPerformanceCounter(`host.paint_yields.${suffix}`);
    }
  }

  addPerformanceCounter(name, delta = 1) {
    addPerformanceCounter(this.performanceCollector, name, delta);
  }

  addPerformanceGauge(name, value) {
    addPerformanceGauge(this.performanceCollector, name, value);
  }

  setPerformanceLast(name, value) {
    setPerformanceLast(this.performanceCollector, name, value);
  }

  configurePerfSentinel(options = {}) {
    const explicit = parseBooleanParam(options.fishyPerf ?? options.perfSentinel);
    const enabled =
      explicit !== undefined
        ? explicit
        : fishyMapPerfEnabled(
            options.locationHref ||
              options.locationLike?.href ||
              globalThis.location?.href ||
              globalThis.window?.location?.href,
          );
    if (enabled && !this.perfSentinel.enabled) {
      this.perfSentinel = createPerfSentinel(true);
    } else if (enabled === false && this.perfSentinel.enabled) {
      this.teardownPerfSentinel();
      this.perfSentinel = createPerfSentinel(false);
    }
    if (this.perfSentinel.enabled) {
      this.installPerfSentinel();
    }
  }

  configurePerformanceCollection(options = {}) {
    this.performanceOptions = normalizePerformanceOptions({
      scenario: options.profileScenario || "load_map",
      captureSpans: options.captureSpans === true || this.perfSentinel?.enabled === true,
      captureFrames: options.captureFrames === true,
    });
    this.performanceCollector = createPerformanceCollector(this.performanceOptions.scenario);
    this.firstFrameReady = false;
  }

  installPerfSentinel() {
    const sentinel = this.perfSentinel;
    if (!sentinel.enabled || sentinel.installed) {
      return;
    }
    sentinel.installed = true;
    sentinel.startedAtMs = performanceNowMs();
    const win = globalThis.window || null;
    win?.addEventListener?.("error", this.boundPerfError);
    win?.addEventListener?.("unhandledrejection", this.boundPerfUnhandledRejection);
    sentinel.listeners.push(["error", this.boundPerfError], ["unhandledrejection", this.boundPerfUnhandledRejection]);

    const PerformanceObserverImpl = globalThis.PerformanceObserver || win?.PerformanceObserver;
    const supportedEntryTypes = PerformanceObserverImpl?.supportedEntryTypes || [];
    if (
      typeof PerformanceObserverImpl === "function" &&
      Array.isArray(supportedEntryTypes) &&
      supportedEntryTypes.includes("long-animation-frame")
    ) {
      try {
        sentinel.loafObserver = new PerformanceObserverImpl((list) => {
          const entries =
            typeof list?.getEntries === "function" ? list.getEntries() : [];
          for (const entry of entries) {
            this.recordLongAnimationFrameEntry(entry);
          }
        });
        sentinel.loafObserver.observe({ type: "long-animation-frame", buffered: true });
        sentinel.loafSupported = true;
      } catch (error) {
        sentinel.loafSupported = false;
        this.recordRecentError(error, { source: "perf.loaf_observer" });
      }
    }
    this.installPerfGlobalApi();
  }

  teardownPerfSentinel() {
    const sentinel = this.perfSentinel;
    const win = globalThis.window || null;
    for (const [type, listener] of sentinel.listeners || []) {
      win?.removeEventListener?.(type, listener);
    }
    sentinel.listeners = [];
    if (sentinel.loafObserver) {
      try {
        sentinel.loafObserver.disconnect();
      } catch (_) {}
    }
    sentinel.loafObserver = null;
    sentinel.installed = false;
  }

  installPerfGlobalApi() {
    const win = globalThis.window || null;
    if (!win) {
      return;
    }
    const bridge = this;
    const api = {
      enabled: true,
      snapshot() {
        return bridge.getFishyPerfSnapshot();
      },
      async copy() {
        const snapshot = bridge.getFishyPerfSnapshot();
        const text = JSON.stringify(snapshot, null, 2);
        const clipboard = win.navigator?.clipboard || globalThis.navigator?.clipboard;
        if (!clipboard?.writeText) {
          throw new Error("fishy perf snapshot copy requires navigator.clipboard.writeText");
        }
        await clipboard.writeText(text);
        return snapshot;
      },
      download(filename = "fishymap-perf-snapshot.json") {
        const snapshot = bridge.getFishyPerfSnapshot();
        const doc = win.document || globalThis.document;
        if (
          !doc?.createElement ||
          typeof Blob !== "function" ||
          !win.URL?.createObjectURL
        ) {
          return snapshot;
        }
        const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
          type: "application/json",
        });
        const url = win.URL.createObjectURL(blob);
        try {
          const link = doc.createElement("a");
          link.href = url;
          link.download = String(filename || "fishymap-perf-snapshot.json");
          link.rel = "noopener";
          link.click?.();
        } finally {
          scheduleBrowserMicrotask(() => win.URL.revokeObjectURL(url));
        }
        return snapshot;
      },
    };
    win.__fishyPerf = api;
  }

  recordRecentError(error, context = {}) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    pushRing(this.perfSentinel.recentErrors, normalizeErrorRecord(error, context));
  }

  recordPerfSentinelEvent(event = {}) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    const sentinel = this.perfSentinel;
    if (event.type === "network") {
      const record = {
        atMs: Number(event.atMs || performanceNowMs()),
        classification: String(event.classification || "other"),
        url: String(event.url || ""),
        method: String(event.method || "GET"),
        cacheMode: String(event.cacheMode || ""),
        status: Number(event.status || 0),
        ok: event.ok === true,
        durationMs: Number(event.durationMs || 0),
        ...(event.error ? { error: String(event.error) } : {}),
      };
      pushRing(sentinel.networkRequests, record);
      sentinel.networkCounts[record.classification] =
        (sentinel.networkCounts[record.classification] || 0) + 1;
      this.addPerformanceCounter(`host.network.requests.${record.classification}`);
      this.addPerformanceGauge("host.network.request_duration_ms", record.durationMs);
      return;
    }
    if (event.type === "cache") {
      const record = {
        atMs: Number(event.atMs || performanceNowMs()),
        operation: String(event.operation || "unknown"),
        ...(event.reason ? { reason: String(event.reason) } : {}),
        ...(event.cacheKey ? { cacheKey: String(event.cacheKey) } : {}),
        assetCount: Number.parseInt(event.assetCount, 10) || 0,
      };
      pushRing(sentinel.cacheOperations, record);
      sentinel.cacheCounts[record.operation] =
        (sentinel.cacheCounts[record.operation] || 0) + 1;
      this.addPerformanceCounter(`host.cache.${normalizePerfCounterSuffix(record.operation) || "unknown"}`);
      return;
    }
    if (event.type === "runtimeManifest") {
      sentinel.runtimeManifest = {
        manifestUrl: String(event.manifestUrl || ""),
        moduleUrl: String(event.moduleUrl || ""),
        wasmUrl: String(event.wasmUrl || ""),
        schemaVersion: Number(event.schemaVersion || 0),
        threaded: event.threaded === true,
        assetCount: Number.parseInt(event.assetCount, 10) || 0,
        threadPoolExport: String(event.threadPoolExport || ""),
      };
      return;
    }
    if (event.type === "threadStatus") {
      sentinel.threadStatus = {
        manifestThreaded: event.manifestThreaded === true,
        workerCount: Number.parseInt(event.workerCount, 10) || 0,
        threadPoolStarted: event.threadPoolStarted === true,
        readiness: isPlainObject(event.readiness) ? cloneJson(event.readiness) : {},
        ...(event.error ? { error: String(event.error) } : {}),
      };
      return;
    }
    if (event.type === "error") {
      this.recordRecentError(event.error || event.message, {
        source: event.source || "perf.event",
      });
    }
  }

  recordLongAnimationFrameEntry(entry) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    const normalized = normalizeLoafEntry(entry);
    pushRing(this.perfSentinel.loaf, normalized);
    this.addPerformanceCounter("host.main_thread.loaf_entries");
    this.addPerformanceGauge("host.main_thread.loaf_duration_ms", normalized.duration);
    this.addPerformanceGauge(
      "host.main_thread.loaf_blocking_duration_ms",
      normalized.blockingDuration,
    );
    this.setPerformanceLast("host.main_thread.loaf_duration_ms", normalized.duration);
    this.setPerformanceLast(
      "host.main_thread.loaf_blocking_duration_ms",
      normalized.blockingDuration,
    );
  }

  recordBridgePayload(direction, label, payload) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    const estimatedBytes = estimateJsonByteLength(payload);
    const record = {
      atMs: performanceNowMs(),
      label: String(label || "unknown"),
      estimatedBytes,
    };
    const eventSummary = summarizeBridgeEventBatch(payload);
    if (eventSummary.eventCount > 0) {
      Object.assign(record, eventSummary);
    }
    const target =
      direction === "ingress"
        ? this.perfSentinel.bridgeIngress
        : this.perfSentinel.bridgeEgress;
    pushRing(target, record);
    this.addPerformanceGauge(`host.bridge.${direction}_payload_bytes`, estimatedBytes);
    this.setPerformanceLast(`host.bridge.${direction}_payload_bytes`, estimatedBytes);
  }

  observeHotBridgePayload(payload, direction = "egress") {
    const devAssertions = globalThis.__fishymapDevAssertions;
    const assertionsEnabled =
      this.perfSentinel?.enabled || devAssertions === true || devAssertions === "throw";
    if (!assertionsEnabled) {
      return true;
    }
    const result = validateFishyMapHotEventPayload(payload);
    if (result.ok) {
      return true;
    }
    const record = {
      atMs: performanceNowMs(),
      direction,
      type: result.type,
      estimatedBytes: result.estimatedBytes,
      limitBytes: result.limitBytes,
      forbiddenPaths: result.forbiddenPaths,
      violations: result.violations,
    };
    pushRing(this.perfSentinel.hotPayloadViolations, record);
    this.addPerformanceCounter("host.v2.hot_payload_violations");
    this.recordRecentError(new Error(`hot payload violation: ${result.violations.join("; ")}`), {
      source: "bridge.hot_payload",
      type: result.type,
    });
    if (devAssertions === "throw") {
      assertFishyMapHotEventPayload(payload);
    }
    return false;
  }

  recordListenerTiming(target, type, durationMs) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    const record = {
      atMs: performanceNowMs(),
      target: String(target || "unknown"),
      type: String(type || ""),
      durationMs: Number(durationMs || 0),
    };
    pushRing(this.perfSentinel.listenerTimings, record);
    this.addPerformanceGauge("host.v2.listener_duration_ms", record.durationMs);
    if (record.durationMs > HOST_V2_LISTENER_BUDGET_MS) {
      this.addPerformanceCounter("host.v2.listeners_over_budget");
    }
  }

  recordShellDrainTiming(events, durationMs, batch = {}) {
    if (!this.perfSentinel?.enabled) {
      return;
    }
    const eventSummary = summarizeBridgeEventBatch({
      ...batch,
      events: Array.isArray(events) ? events : [],
    });
    const record = {
      atMs: performanceNowMs(),
      durationMs: Number(durationMs || 0),
      estimatedBytes: estimateJsonByteLength({
        ...batch,
        events: Array.isArray(events) ? events : [],
      }),
      ...eventSummary,
    };
    pushRing(this.perfSentinel.shellDrains, record);
  }

  getFishyPerfSnapshot(options = {}) {
    const sentinel = this.perfSentinel || createPerfSentinel(false);
    const host = snapshotPerformanceCollector(this.performanceCollector);
    const bevySummary =
      Object.prototype.hasOwnProperty.call(options, "bevySummary")
        ? options.bevySummary
        : this.readWasmPerformanceSummary();
    const telemetry =
      Object.prototype.hasOwnProperty.call(options, "telemetry")
        ? options.telemetry
        : this.readWasmTelemetrySample();
    const hostRaf =
      Object.prototype.hasOwnProperty.call(options, "hostRaf")
        ? options.hostRaf
        : summarizeHostRafMetrics(this.rafMetrics);
    const mainThreadPressure = summarizeMainThreadPressure(this.rafMetrics, sentinel);
    Object.assign(
      host.counters,
      hostRafCounterSnapshot(hostRaf),
      mainThreadPressureCounterSnapshot(mainThreadPressure),
    );
    return {
      enabled: sentinel.enabled === true,
      capturedAt: new Date().toISOString(),
      elapsedMs: Math.max(0, performanceNowMs() - (sentinel.startedAtMs || performanceNowMs())),
      hostRaf,
      mainThreadPressure,
      loaf: {
        supported: sentinel.loafSupported === true,
        entries: cloneJson(sentinel.loaf || []),
      },
      bevy: {
        summary: bevySummary ? cloneJson(bevySummary) : null,
        telemetry: telemetry ? cloneJson(telemetry) : null,
      },
      bridge: {
        host,
        ingress: cloneJson(sentinel.bridgeIngress || []),
        egress: cloneJson(sentinel.bridgeEgress || []),
        listenerTimings: cloneJson(sentinel.listenerTimings || []),
        shellDrains: cloneJson(sentinel.shellDrains || []),
        hotPayloadViolations: cloneJson(sentinel.hotPayloadViolations || []),
      },
      network: {
        counts: cloneJson(sentinel.networkCounts || {}),
        requests: cloneJson(sentinel.networkRequests || []),
      },
      cache: {
        counts: cloneJson(sentinel.cacheCounts || {}),
        operations: cloneJson(sentinel.cacheOperations || []),
      },
      runtimeManifest: cloneJson(sentinel.runtimeManifest || {}),
      threadStatus: cloneJson(
        sentinel.threadStatus || {
          manifestThreaded: this.resourceDiscovery?.runtimeManifestThreaded === true,
          workerCount: this.runtimeSettings?.wasmThreadCount || 0,
          threadPoolStarted: false,
          readiness: {
            crossOriginIsolated: this.resourceDiscovery?.crossOriginIsolated === true,
            sharedArrayBufferAvailable:
              this.resourceDiscovery?.sharedArrayBufferAvailable === true,
            secureContext: this.resourceDiscovery?.secureContext === true,
          },
        },
      ),
      recentErrors: cloneJson(sentinel.recentErrors || []),
    };
  }

  startHostRafMetrics() {
    if (this.rafMetrics.running) {
      return;
    }
    const requestAnimationFrameImpl = globalThis.window?.requestAnimationFrame;
    if (typeof requestAnimationFrameImpl !== "function") {
      this.setPerformanceLast("host.raf.available", 0);
      return;
    }
    this.setPerformanceLast("host.raf.available", 1);
    this.rafMetrics = createHostRafMetrics();
    this.rafMetrics.running = true;
    this.rafMetrics.startedAtMs = performanceNowMs();
    const tick = (timestampMs) => {
      if (!this.rafMetrics.running) {
        this.rafMetricsTimer = 0;
        return;
      }
      this.recordHostRafFrame(timestampMs);
      this.rafMetricsTimer = requestAnimationFrameImpl.call(globalThis.window, tick) || 0;
    };
    this.rafMetricsTimer = requestAnimationFrameImpl.call(globalThis.window, tick) || 0;
  }

  stopHostRafMetrics() {
    this.rafMetrics.running = false;
    const cancelAnimationFrameImpl = globalThis.window?.cancelAnimationFrame;
    if (this.rafMetricsTimer && typeof cancelAnimationFrameImpl === "function") {
      cancelAnimationFrameImpl.call(globalThis.window, this.rafMetricsTimer);
    }
    this.rafMetricsTimer = 0;
  }

  freezePerformanceObservationForExport() {
    this.stopHostRafMetrics();
    const sentinel = this.perfSentinel;
    if (!sentinel?.loafObserver) {
      return;
    }
    try {
      sentinel.loafObserver.disconnect();
    } catch (_) {}
    sentinel.loafObserver = null;
  }

  recordHostRafFrame(timestampMs) {
    const nowMs = Number.isFinite(Number(timestampMs)) ? Number(timestampMs) : performanceNowMs();
    const metrics = this.rafMetrics;
    if (metrics.lastFrameMs != null) {
      const gapMs = Math.max(0, nowMs - metrics.lastFrameMs);
      metrics.gapsMs.push(gapMs);
      if (metrics.gapsMs.length > 240) {
        metrics.gapsMs.shift();
      }
      metrics.maxGapMs = Math.max(metrics.maxGapMs, gapMs);
    }
    metrics.lastFrameMs = nowMs;
    metrics.frameCount += 1;
    const elapsedSeconds = Math.max(0, (nowMs - metrics.startedAtMs) / 1000);
    if (elapsedSeconds > 0) {
      metrics.lastFps = metrics.frameCount / elapsedSeconds;
    }
  }

  addV2InputSequence() {
    this.v2InputSequence += 1;
    return this.v2InputSequence;
  }

  addV2CommandSequence() {
    this.v2CommandSequence += 1;
    return this.v2CommandSequence;
  }

  addV2OutputSequence() {
    this.v2OutputSequence += 1;
    return this.v2OutputSequence;
  }

  shellV2CoalesceKey(event) {
    const type = String(event?.type || "");
    if (
      type === "view-changed" ||
      type === "hover-changed" ||
      type === "selection-changed" ||
      type === "hotspot-visibility-changed"
    ) {
      return type;
    }
    return "";
  }

  recordShellV2QueueDepth() {
    const depth = this.pendingShellV2Events.length;
    this.addPerformanceGauge("host.v2.shell_queue_depth", depth);
    this.setPerformanceLast("host.v2.shell_queue_depth", depth);
  }

  queueShellV2Events(events, { appliedInputSequence = 0 } = {}) {
    if (!Array.isArray(events)) {
      throw new TypeError("FishyMap shell events must be an array");
    }
    for (const event of events) {
      this.observeHotBridgePayload(event, "shell");
      const coalesceKey = this.shellV2CoalesceKey(event);
      if (coalesceKey) {
        const before = this.pendingShellV2Events.length;
        this.pendingShellV2Events = this.pendingShellV2Events.filter(
          (queued) => this.shellV2CoalesceKey(queued) !== coalesceKey,
        );
        const coalesced = before - this.pendingShellV2Events.length;
        if (coalesced > 0) {
          const suffix = normalizePerfCounterSuffix(coalesceKey) || "unknown";
          this.addPerformanceCounter(`host.v2.shell_events_coalesced.${suffix}`, coalesced);
          this.addPerformanceCounter("host.v2.shell_events_coalesced", coalesced);
        }
      }
      this.pendingShellV2Events.push(cloneJson(event));
    }
    if (appliedInputSequence) {
      this.pendingShellAppliedInputSequence = Math.max(
        this.pendingShellAppliedInputSequence,
        appliedInputSequence,
      );
    }
    this.recordShellV2QueueDepth();
    this.schedulePostSinkDrain();
  }

  schedulePostSinkDrain() {
    if (this.postSinkDrainTask || this.drainingPostSink) {
      return;
    }
    this.postSinkDrainTask = scheduleBrowserMicrotask(() => {
      this.postSinkDrainTask = null;
      this.drainingPostSink = true;
      try {
        this.runtimeInputReleasePending = false;
        this.#flushPendingStatePatch();
        this.flushQueuedCommands();
        if (this.deferredStateRefreshKind) {
          this.flushDeferredStateRefresh();
        }
        this.flushShellV2Events();
      } finally {
        this.drainingPostSink = false;
      }
      if (
        this.runtimeInputReleasePending ||
        this.deferredStateRefreshKind ||
        this.pendingShellV2Events.length ||
        this.pendingShellAppliedInputSequence
      ) {
        this.schedulePostSinkDrain();
      }
    });
  }

  flushShellV2Events() {
    if (!this.pendingShellV2Events.length && !this.pendingShellAppliedInputSequence) {
      this.recordShellV2QueueDepth();
      return;
    }
    const startedAtMs = performanceNowMs();
    const events = this.pendingShellV2Events.splice(0, this.pendingShellV2Events.length);
    const appliedInputSequence = this.pendingShellAppliedInputSequence || null;
    this.pendingShellAppliedInputSequence = 0;
    this.recordShellV2QueueDepth();
    const batch = {
      version: FISHYMAP_BRIDGE_V2_VERSION,
      runtimeId: null,
      sequence: this.addV2OutputSequence(),
      ...(appliedInputSequence != null ? { appliedInputSequence } : {}),
      frameId: null,
      monotonicTimeMs: performanceNowMs(),
      stateRevisions: { ...this.stateRevisions },
      events,
      droppedEventSummary: null,
      bridgeStatsDelta: null,
    };
    this.recordBridgePayload("egress", "shell-v2-event-batch", batch);
    this.dispatchShellV2Batch(batch);
    const durationMs = performanceNowMs() - startedAtMs;
    this.recordShellDrainTiming(events, durationMs, batch);
    addPerformanceSpanSample(this.performanceCollector, "host.v2.shell_drain", durationMs);
    this.setPerformanceLast("host.v2.shell_drain_duration_ms", durationMs);
    this.setPerformanceLast("host.v2.shell_drain_budget_ms", HOST_V2_SHELL_DRAIN_BUDGET_MS);
    this.addPerformanceCounter("host.v2.shell_drains");
    this.addPerformanceCounter("host.v2.shell_events_dispatched", events.length);
    if (durationMs > HOST_V2_SHELL_DRAIN_BUDGET_MS) {
      this.addPerformanceCounter("host.v2.shell_drains_over_budget");
    }
  }

  reportLoadProgress(detail) {
    notifyMapRuntimeLoadProgress(this.loadProgressCallback, detail);
  }

  mountedCanvasFor(container, options = {}) {
    return (
      options.canvas ||
      container?.querySelector?.("canvas") ||
      globalThis.document?.getElementById?.("bevy") ||
      null
    );
  }

  canReuseMountedRuntime(container, canvas) {
    return Boolean(
      this.wasmReady &&
        this.wasmModule &&
        this.container === container &&
        this.canvas &&
        (!canvas || this.canvas === canvas),
    );
  }

  applyMountOptionsToLiveRuntime(container, canvas, options = {}) {
    this.container = container;
    this.canvas = canvas || this.canvas;
    if (this.canvas && !this.canvas.hasAttribute("tabindex")) {
      this.canvas.tabIndex = 0;
    }
    this.loadProgressCallback = options.onLoadProgress || this.loadProgressCallback;
  }

  reuseMountedRuntime(container, canvas, options = {}) {
    this.addPerformanceCounter("host.mount.reused_live_runtime");
    this.setPerformanceLast("host.mount_reused_live_runtime", 1);
    this.applyMountOptionsToLiveRuntime(container, canvas, options);
    this.startHostRafMetrics();
    this.reportLoadProgress({
      stage: "runtime-resume",
      progress: this.firstFrameReady ? 1 : 0.96,
    });
    this.wasmModule.fishymap_v2_set_event_sink(this.boundEventSink);
    this.syncCanvasSize();
    this.syncTheme();
    this.refreshCurrentStateFromWasm();

    const startupPatch = buildStartupStatePatch(
      this.runtimeSettings.memoryBudgetMiB,
      options.initialState,
    );
    if (isMeaningfulPatch(startupPatch)) {
      this.setState(startupPatch);
    }
    this.flushQueuedCommands();
    this.#flushPendingStatePatch();
    this.flushQueuedCommands();
    return this.getCurrentState();
  }

  markFirstFrameReady() {
    if (this.firstFrameReady) {
      return false;
    }
    this.firstFrameReady = true;
    this.setPerformanceLast(
      "host.first_frame_elapsed_ms",
      performanceNowMs() - (this.performanceCollector?.startedAtMs || performanceNowMs()),
    );
    this.reportLoadProgress({
      stage: "first-paint",
      progress: 0.98,
    });
    return true;
  }

  syncWasmProfiling(wasmModule = this.wasmModule) {
    if (!wasmModule?.fishymap_reset_profiling_json) {
      return;
    }
    wasmModule.fishymap_reset_profiling_json(
      JSON.stringify({
        scenario: this.performanceOptions.scenario,
        warmupFrames: this.performanceOptions.warmupFrames,
        captureTrace: this.performanceOptions.captureTrace,
        captureSpans: this.performanceOptions.captureSpans,
        captureFrames: this.performanceOptions.captureFrames,
      }),
    );
  }

  publishRuntimeResourceDiscovery(runtimeModule) {
    const discovery = collectMapRuntimeResourceDiscovery({
      globalLike: runtimeModule?.globalLike ?? globalThis,
      locationLike: runtimeModule?.locationLike ?? globalThis.location,
      canvas: this.canvas,
      manifestInfo: runtimeModule?.manifestInfo,
      wasmThreadCount: runtimeModule?.workerCount || 0,
      memoryBudgetMiB: runtimeModule?.memoryBudgetMiB ?? this.runtimeSettings.memoryBudgetMiB,
    });
    this.resourceDiscovery = {
      ...(runtimeModule?.resourceDiscovery || {}),
      ...discovery,
      canvasWidth: discovery.canvasWidth,
      canvasHeight: discovery.canvasHeight,
    };
    this.runtimeSettings = {
      wasmThreadCount: this.resourceDiscovery.wasmThreadCount,
      memoryBudgetMiB: this.resourceDiscovery.memoryBudgetMiB,
    };
    this.setPerformanceLast(
      "host.runtime_cross_origin_isolated",
      this.resourceDiscovery.crossOriginIsolated ? 1 : 0,
    );
    this.setPerformanceLast(
      "host.runtime_shared_array_buffer_available",
      this.resourceDiscovery.sharedArrayBufferAvailable ? 1 : 0,
    );
    this.setPerformanceLast(
      "host.runtime_secure_context",
      this.resourceDiscovery.secureContext ? 1 : 0,
    );
    this.setPerformanceLast(
      "host.runtime_hardware_concurrency",
      this.resourceDiscovery.hardwareConcurrency || 0,
    );
    this.setPerformanceLast("host.runtime_device_memory_gib", this.resourceDiscovery.deviceMemory || 0);
    this.setPerformanceLast(
      "host.runtime_device_pixel_ratio",
      this.resourceDiscovery.devicePixelRatio || 1,
    );
    this.setPerformanceLast("host.runtime_canvas_width", this.resourceDiscovery.canvasWidth || 0);
    this.setPerformanceLast("host.runtime_canvas_height", this.resourceDiscovery.canvasHeight || 0);
    this.setPerformanceLast(
      "host.runtime_manifest_schema_version",
      this.resourceDiscovery.runtimeManifestVersion || 0,
    );
    this.setPerformanceLast(
      "host.runtime_memory_budget_mib",
      this.resourceDiscovery.memoryBudgetMiB || 0,
    );
    if (runtimeModule?.manifestInfo) {
      this.recordPerfSentinelEvent({
        type: "runtimeManifest",
        manifestUrl: runtimeModule.manifestInfo.manifestUrl,
        moduleUrl: runtimeModule.manifestInfo.moduleUrl,
        wasmUrl: runtimeModule.manifestInfo.wasmUrl,
        schemaVersion:
          Number(
            runtimeModule.manifestInfo.manifest?.schemaVersion ??
              runtimeModule.manifestInfo.manifest?.schema_version ??
              0,
          ) || 0,
        threaded: runtimeModule.manifestInfo.threaded === true,
        assetCount: runtimeModule.manifestInfo.assets?.length || 0,
        threadPoolExport: runtimeModule.manifestInfo.threadPoolExport || "",
      });
    }
    this.recordPerfSentinelEvent({
      type: "threadStatus",
      manifestThreaded: runtimeModule?.threaded === true,
      workerCount: runtimeModule?.workerCount || 0,
      threadPoolStarted: false,
      readiness: runtimeModule?.threadingReadiness || {
        crossOriginIsolated: this.resourceDiscovery.crossOriginIsolated,
        sharedArrayBufferAvailable: this.resourceDiscovery.sharedArrayBufferAvailable,
        secureContext: this.resourceDiscovery.secureContext,
        hardwareConcurrency: this.resourceDiscovery.hardwareConcurrency,
        deviceMemory: this.resourceDiscovery.deviceMemory,
      },
    });
  }

  async initializeThreadPoolIfNeeded(wasmModule, runtimeModule) {
    const threaded = runtimeModule?.threaded === true;
    this.setPerformanceLast("host.runtime_threaded", threaded ? 1 : 0);
    this.setPerformanceLast("host.runtime_worker_count", runtimeModule?.workerCount || 0);
    this.publishRuntimeResourceDiscovery(runtimeModule);
    if (!threaded) {
      return;
    }
    const exportName = runtimeModule.threadPoolExport || "fishymap_init_thread_pool";
    const initThreadPool = wasmModule?.[exportName];
    if (typeof initThreadPool !== "function") {
      this.addPerformanceCounter("host.runtime_thread_pool_init_failure");
      this.recordPerfSentinelEvent({
        type: "threadStatus",
        manifestThreaded: true,
        workerCount: runtimeModule?.workerCount || 0,
        threadPoolStarted: false,
        readiness: runtimeModule?.threadingReadiness || {},
        error: `missing ${exportName} export`,
      });
      throw new Error(`threaded map runtime missing ${exportName} export`);
    }
    try {
      await this.measurePerformanceSpanAsync("host.thread_pool_init", () =>
        initThreadPool(runtimeModule.workerCount),
      );
      this.recordPerfSentinelEvent({
        type: "threadStatus",
        manifestThreaded: true,
        workerCount: runtimeModule?.workerCount || 0,
        threadPoolStarted: true,
        readiness: runtimeModule?.threadingReadiness || {},
      });
    } catch (error) {
      this.addPerformanceCounter("host.runtime_thread_pool_init_failure");
      this.recordPerfSentinelEvent({
        type: "threadStatus",
        manifestThreaded: true,
        workerCount: runtimeModule?.workerCount || 0,
        threadPoolStarted: false,
        readiness: runtimeModule?.threadingReadiness || {},
        error: error && typeof error === "object" && "message" in error
          ? String(error.message)
          : String(error),
      });
      throw error;
    }
  }

  async loadAndInitializeRuntime(options = {}, preparation = null) {
    const runtimeModule = await this.measurePerformanceSpanAsync("host.runtime_module_load", () =>
      loadMapRuntimeModule({
        ...options,
        onLoadProgress: (detail) => this.reportLoadProgress(detail),
        onPerfEvent: (event) => this.recordPerfSentinelEvent(event),
      }),
    );
    if (preparation?.cancelled) {
      throw new Error("FishyMap runtime preparation was cancelled");
    }
    await this.yieldToBrowser("runtime-module-loaded");
    if (preparation?.cancelled) {
      throw new Error("FishyMap runtime preparation was cancelled");
    }
    const initializedRuntime = await this.initializeRuntimeModule(runtimeModule);
    if (preparation?.cancelled) {
      throw new Error("FishyMap runtime preparation was cancelled");
    }
    return initializedRuntime;
  }

  async initializeRuntimeModule(runtimeModule) {
    const wasmModule = runtimeModule.wasmModule || runtimeModule;
    this.reportLoadProgress({
      stage: "wasm-compile",
      progress: 0.82,
    });
    await this.yieldToBrowserPaint("before-wasm-init", { frames: 2 });
    try {
      const initInput =
        runtimeModule.wasmInitInput === undefined
          ? undefined
          : { module_or_path: runtimeModule.wasmInitInput };
      await this.measurePerformanceSpanAsync("host.wasm_init", () =>
        wasmModule.default(initInput),
      );
    } catch (error) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String(error.message)
          : String(error);
      if (!message.includes("Using exceptions for control flow")) {
        throw error;
      }
    }
    await this.yieldToBrowser("wasm-init-complete");
    return { runtimeModule, wasmModule };
  }

  prepareRuntime(options = {}) {
    if (this.runtimePreparation) {
      this.loadProgressCallback = options.onLoadProgress || this.loadProgressCallback;
      return this.runtimePreparation.promise;
    }
    if (this.container || this.wasmReady || this.rendererStarted) {
      throw new Error("FishyMapBridge.prepareRuntime must run before mount");
    }
    if (options.wasmModule) {
      throw new Error("FishyMapBridge.prepareRuntime does not accept an injected wasm module");
    }

    this.configurePerfSentinel(options);
    this.configurePerformanceCollection(options);
    this.loadProgressCallback = options.onLoadProgress;
    const preparation = {
      cancelled: false,
      promise: null,
    };
    this.runtimePreparation = preparation;
    preparation.promise = this.loadAndInitializeRuntime(options, preparation);
    // The later mount consumes the original rejection. This handler prevents an
    // early network failure from becoming an unhandled rejection while the page
    // restores its persisted state.
    void preparation.promise.catch(() => {});
    return preparation.promise;
  }

  async mount(container, options = {}) {
    this.configurePerfSentinel(options);
    const runtimePreparation = this.runtimePreparation;
    const mountPromise = this.measurePerformanceSpanAsync("host.mount", async () => {
      if (!container) {
        throw new Error("FishyMapBridge.mount requires a container element");
      }
      if (runtimePreparation && options.wasmModule) {
        throw new Error("FishyMapBridge.mount cannot replace a prepared runtime");
      }
      const requestedCanvas = this.mountedCanvasFor(container, options);
      if (this.canReuseMountedRuntime(container, requestedCanvas)) {
        return this.measurePerformanceSpan("host.mount_reuse", () =>
          this.reuseMountedRuntime(container, requestedCanvas, options),
        );
      }
      if (this.container) {
        this.setPerformanceLast("host.mount_reused_live_runtime", 0);
        if (
          (this.wasmReady || this.rendererStarted) &&
          this.canvas &&
          requestedCanvas &&
          this.canvas !== requestedCanvas
        ) {
          this.addPerformanceCounter("host.mount.live_runtime_retarget_rejected");
          throw new Error(
            "FishyMapBridge cannot retarget a live Bevy web runtime; reload the page to use a different canvas or runtime mode",
          );
        }
        this.destroy();
      }
      this.setPerformanceLast("host.mount_reused_live_runtime", 0);
      if (!runtimePreparation) {
        this.configurePerformanceCollection(options);
      }
      this.container = container;
      this.canvas = requestedCanvas;
      if (this.canvas && !this.canvas.hasAttribute("tabindex")) {
        this.canvas.tabIndex = 0;
      }

      this.attachDomListeners();
      this.installCanvasSizeSync();
      this.installThemeSync();
      await this.yieldToBrowser("dom-ready");

      this.loadProgressCallback = options.onLoadProgress || this.loadProgressCallback;
      const injectedThreadingReadiness = options.wasmModule
        ? detectWasmThreadingReadiness(options.globalLike ?? globalThis)
        : null;
      const injectedMemoryBudgetMiB = options.wasmModule
        ? resolveMapRuntimeMemoryBudgetMiB({
            locationLike: options.locationLike ?? globalThis.location,
            explicitValue: options.memoryBudgetMiB,
            deviceMemory: injectedThreadingReadiness?.deviceMemory,
          })
        : 0;
      let initializedRuntime;
      if (options.wasmModule) {
        const runtimeModule = {
          wasmModule: options.wasmModule,
          wasmInitInput: options.wasmInitInput,
          threaded: false,
          threadPoolExport: "",
          threadingReadiness: injectedThreadingReadiness,
          workerCount: 0,
          memoryBudgetMiB: injectedMemoryBudgetMiB,
          resourceDiscovery: collectMapRuntimeResourceDiscovery({
            globalLike: options.globalLike ?? globalThis,
            locationLike: options.locationLike ?? globalThis.location,
            canvas: this.canvas,
            wasmThreadCount: 0,
            memoryBudgetMiB: injectedMemoryBudgetMiB,
          }),
          globalLike: options.globalLike ?? globalThis,
          locationLike: options.locationLike ?? globalThis.location,
        };
        await this.yieldToBrowser("runtime-module-loaded");
        initializedRuntime = await this.initializeRuntimeModule(runtimeModule);
      } else if (runtimePreparation) {
        try {
          initializedRuntime = await runtimePreparation.promise;
        } catch (error) {
          if (this.runtimePreparation === runtimePreparation) {
            this.runtimePreparation = null;
          }
          throw error;
        }
      } else {
        initializedRuntime = await this.loadAndInitializeRuntime(options);
      }
      if (this.runtimePreparation === runtimePreparation) {
        this.runtimePreparation = null;
      }
      const { runtimeModule, wasmModule } = initializedRuntime;
      await this.initializeThreadPoolIfNeeded(wasmModule, runtimeModule);
      this.reportLoadProgress({
        stage: "renderer-start",
        progress: 0.9,
      });
      this.wasmModule = wasmModule;
      assertBridgeV2Exports(wasmModule);
      wasmModule.fishymap_v2_set_event_sink(this.boundEventSink);
      this.syncWasmProfiling(wasmModule);
      if (!this.rendererStarted && typeof wasmModule.fishymap_mount === "function") {
        await this.yieldToBrowserPaint("before-renderer-mount");
        this.measurePerformanceSpan("host.renderer_mount", () => {
          wasmModule.fishymap_mount();
        });
        this.rendererStarted = true;
      }
      this.observeBrowserYield("renderer-mounted");
      this.wasmReady = true;
      this.startHostRafMetrics();
      this.refreshCurrentStateFromWasm();
      this.inputState.ui.viewMode = this.currentState.view?.viewMode === "3d" ? "3d" : "2d";

      const startupPatch = buildStartupStatePatch(
        this.runtimeSettings.memoryBudgetMiB,
        options.initialState,
      );
      if (isMeaningfulPatch(startupPatch)) {
        this.setState(startupPatch);
      }
      this.syncTheme();
      this.reportLoadProgress({
        stage: "runtime-bootstrap",
        progress: 0.96,
      });
      return this.getCurrentState();
    });
    try {
      return await mountPromise;
    } catch (error) {
      this.recordRecentError(error, { source: "host.mount" });
      throw error;
    }
  }

  destroy() {
    this.measurePerformanceSpan("host.destroy", () => {
      if (this.runtimePreparation) {
        this.runtimePreparation.cancelled = true;
        this.runtimePreparation = null;
      }
      if (this.postSinkDrainTask) {
        cancelBrowserTask(this.postSinkDrainTask);
      }
      this.stopHostRafMetrics();
      this.teardownPerfSentinel();
      this.deferredStateRefreshKind = "";
      this.postSinkDrainTask = null;
      this.drainingPostSink = false;
      this.rafMetricsTimer = 0;
      this.pendingShellV2Events = [];
      this.pendingShellAppliedInputSequence = 0;
      this.rafMetrics = createHostRafMetrics();
      this.stateRevisions = {};
      this.detachDomListeners();
      this.teardownCanvasSizeSync();
      this.teardownThemeSync();

      if (this.wasmModule?.fishymap_destroy) {
        this.wasmModule.fishymap_destroy();
      } else if (this.wasmModule?.fishymap_v2_clear_event_sink) {
        this.wasmModule.fishymap_v2_clear_event_sink();
      }

      this.wasmModule = null;
      this.wasmReady = false;
      this.runtimeInputReady = false;
      this.runtimeInputReleasePending = false;
      this.firstFrameReady = false;
      this.loadProgressCallback = null;
      this.container = null;
      this.canvas = null;
      this.pendingStatePatch = normalizeStatePatch({});
      this.pendingCommands = [];
      this.v2InputSequence = 0;
      this.v2CommandSequence = 0;
      this.appliedInputSequence = 0;
      this.inputState = createEmptyInputState();
      this.currentState = createEmptySnapshot();
      this.runtimeSettings = {
        wasmThreadCount: 0,
        memoryBudgetMiB: 256,
      };
      this.resourceDiscovery = null;
    });
  }

  setState(patch) {
    return this.measurePerformanceSpan("host.set_state", () => {
      const normalized = normalizeStatePatch(patch);
      if (!patchHasStateFields(normalized) && !patchHasCommands(normalized)) {
        return null;
      }

      let inputSequence = null;
      if (patchHasStateFields(normalized)) {
        const nextInputState = applyStatePatch(this.inputState, normalized);
        const nextDesiredViewMode = nextInputState.ui?.viewMode ?? null;
        if (nextInputState !== this.inputState) {
          this.inputState = nextInputState;
          this.pendingStatePatch = mergeStatePatch(
            this.pendingStatePatch,
            patchWithoutCommands(normalized),
          );
          this.addPerformanceCounter("host.patches.queued");
          inputSequence = this.#flushPendingStatePatch();
        }
        if (
          nextDesiredViewMode &&
          nextDesiredViewMode !== (this.currentState.view?.viewMode === "3d" ? "3d" : "2d") &&
          !(normalized.commands && normalized.commands.setViewMode)
        ) {
          this.#flushPendingStatePatch();
          this.sendCommand({ setViewMode: nextDesiredViewMode });
        }
      }

      if (patchHasCommands(normalized)) {
        inputSequence = this.#flushPendingStatePatch() ?? inputSequence;
        this.sendCommand(normalized.commands);
      }
      return inputSequence;
    });
  }

  sendCommand(command) {
    this.measurePerformanceSpan("host.send_command", () => {
      const normalized = normalizeStatePatch({ commands: command }).commands;
      if (!normalized || !Object.keys(normalized).length) {
        return;
      }
      if (normalized.setViewMode === "2d" || normalized.setViewMode === "3d") {
        this.currentState = {
          ...this.currentState,
          view: {
            ...this.currentState.view,
            viewMode: normalized.setViewMode,
          },
        };
        this.inputState = {
          ...this.inputState,
          ui: {
            ...this.inputState.ui,
            viewMode: normalized.setViewMode,
          },
        };
      }
      this.addPerformanceCounter("host.commands.sent");
      this.pendingCommands.push(normalized);
      if (
        !this.#runtimeInputDeliveryReady() ||
        !this.wasmModule?.fishymap_v2_send_command
      ) {
        this.addPerformanceCounter("host.commands.buffered");
        return;
      }
      this.flushQueuedCommands();
    });
  }

  getCurrentState() {
    return cloneJson(this.currentState);
  }

  requestSnapshot(request = {}) {
    return this.measurePerformanceSpan("host.request_snapshot", () => {
      if (!this.wasmReady || !this.wasmModule?.fishymap_v2_request_snapshot) {
        return null;
      }
      const normalized = normalizeFishyMapV2SnapshotRequest(request);
      this.addPerformanceCounter("host.v2.snapshots_requested");
      const response = this.wasmModule.fishymap_v2_request_snapshot(normalized);
      this.applySnapshotResponse(response);
      return cloneJson(response);
    });
  }

  getTelemetrySample() {
    const runtime = this.readWasmTelemetrySample();
    if (!runtime) {
      return null;
    }
    const raf = summarizeHostRafMetrics(this.rafMetrics);
    const mainThreadPressure = summarizeMainThreadPressure(this.rafMetrics, this.perfSentinel);
    return {
      runtime,
      host: { raf, mainThreadPressure },
    };
  }

  resetPerformanceSnapshot(options = {}) {
    this.performanceOptions = normalizePerformanceOptions({
      ...this.performanceOptions,
      captureSpans: true,
      captureFrames: true,
      ...options,
    });
    this.performanceCollector = createPerformanceCollector(this.performanceOptions.scenario);
    this.stopHostRafMetrics();
    this.startHostRafMetrics();
    this.syncWasmProfiling();
    return {
      scenario: this.performanceOptions.scenario,
      warmupFrames: this.performanceOptions.warmupFrames,
      captureTrace: this.performanceOptions.captureTrace,
      captureSpans: this.performanceOptions.captureSpans,
      captureFrames: this.performanceOptions.captureFrames,
    };
  }

  readWasmPerformanceSummary() {
    if (!this.wasmReady || !this.wasmModule?.fishymap_get_profiling_summary_json) {
      return null;
    }
    const summary = JSON.parse(this.wasmModule.fishymap_get_profiling_summary_json());
    if (!isPlainObject(summary)) {
      throw new TypeError("map profiling summary must be an object");
    }
    return summary;
  }

  readWasmTelemetrySample() {
    if (!this.wasmReady || !this.wasmModule?.fishymap_v2_get_telemetry) {
      return null;
    }
    return this.wasmModule.fishymap_v2_get_telemetry({
      version: FISHYMAP_BRIDGE_V2_VERSION,
    });
  }

  getPerformanceSnapshot(options = {}) {
    if (options.freezeObservation === true) {
      this.freezePerformanceObservationForExport();
    }
    const host = snapshotPerformanceCollector(this.performanceCollector);
    const hostRaf = summarizeHostRafMetrics(this.rafMetrics);
    const wasm =
      this.readWasmPerformanceSummary() ||
      emptyProfileSummary(host.scenario, this.performanceOptions.warmupFrames);
    const telemetry = this.perfSentinel?.enabled ? this.readWasmTelemetrySample() : null;
    const mainThreadPressure = summarizeMainThreadPressure(this.rafMetrics, this.perfSentinel);
    Object.assign(
      host.counters,
      hostRafCounterSnapshot(hostRaf),
      mainThreadPressureCounterSnapshot(mainThreadPressure),
    );
    return {
      scenario: wasm.scenario || host.scenario,
      bevy_version: wasm.bevy_version ?? null,
      git_revision: wasm.git_revision ?? null,
      build_profile: wasm.build_profile ?? "browser",
      frames: wasm.frames ?? 0,
      warmup_frames: wasm.warmup_frames ?? this.performanceOptions.warmupFrames,
      wall_clock_ms: wasm.wall_clock_ms ?? host.elapsed_ms,
      browser_elapsed_ms: host.elapsed_ms,
      frame_time_ms: wasm.frame_time_ms || emptyQuantileSummary(),
      named_spans: {
        ...host.named_spans,
        ...(wasm.named_spans || {}),
      },
      counters: {
        ...host.counters,
        ...(wasm.counters || {}),
      },
      bridge_state: cloneJson(this.currentState),
      resource_discovery: cloneJson(this.resourceDiscovery || {}),
      runtime_settings: cloneJson(this.runtimeSettings),
      ...(this.perfSentinel?.enabled
        ? {
            fishy_perf: this.getFishyPerfSnapshot({
              bevySummary: wasm,
              telemetry,
              hostRaf,
            }),
          }
        : {}),
      host,
      wasm,
    };
  }

  dispatchShellV2Batch(detail) {
    this.measurePerformanceSpan("host.emit", () => {
      this.addPerformanceCounter("host.events.emitted.fishymap_v2");
      if (!this.container?.dispatchEvent) {
        return;
      }
      const containerStartedAtMs = performanceNowMs();
      this.container.dispatchEvent(createCustomEvent(FISHYMAP_EVENTS.v2, detail));
      this.recordListenerTiming(
        "map-shell",
        FISHYMAP_EVENTS.v2,
        performanceNowMs() - containerStartedAtMs,
      );
    });
  }

  attachDomListeners() {
    if (!this.container) {
      return;
    }
    this.canvas?.addEventListener?.("contextmenu", this.boundCanvasContextMenu);
    this.canvas?.addEventListener?.("pointerdown", this.boundCanvasPointerDown);
    globalThis.document?.addEventListener?.("keydown", this.boundDocumentKeyDown, {
      capture: true,
    });
  }

  detachDomListeners() {
    if (!this.container) {
      return;
    }
    this.canvas?.removeEventListener?.("contextmenu", this.boundCanvasContextMenu);
    this.canvas?.removeEventListener?.("pointerdown", this.boundCanvasPointerDown);
    globalThis.document?.removeEventListener?.("keydown", this.boundDocumentKeyDown, {
      capture: true,
    });
  }

  installCanvasSizeSync() {
    this.syncCanvasSize();
    if (globalThis.window?.addEventListener) {
      globalThis.window.addEventListener("resize", this.boundResize, { passive: true });
    }
    const resizeTarget = this.container || this.canvas?.parentElement || null;
    if (resizeTarget && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.syncCanvasSize());
      this.resizeObserver.observe(resizeTarget);
    }
  }

  teardownCanvasSizeSync() {
    globalThis.window?.removeEventListener?.("resize", this.boundResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  syncCanvasSize() {
    if (!this.canvas) {
      return;
    }
    const measurementTarget = this.container || this.canvas.parentElement || this.canvas;
    const targetRect = measurementTarget?.getBoundingClientRect?.() || {};
    const canvasRect = this.canvas.getBoundingClientRect?.() || {};
    const logicalWidth = Math.max(
      1,
      Math.round(
        targetRect.width ||
          canvasRect.width ||
          measurementTarget?.clientWidth ||
          this.canvas.clientWidth ||
          0,
      ),
    );
    const logicalHeight = Math.max(
      1,
      Math.round(
        targetRect.height ||
          canvasRect.height ||
          measurementTarget?.clientHeight ||
          this.canvas.clientHeight ||
          0,
      ),
    );
    const dpr = Math.max(1, globalThis.window?.devicePixelRatio || 1);
    const physicalWidth = Math.max(1, Math.round(logicalWidth * dpr));
    const physicalHeight = Math.max(1, Math.round(logicalHeight * dpr));
    this.canvas.style.width = `${logicalWidth}px`;
    this.canvas.style.height = `${logicalHeight}px`;
    if (this.canvas.width !== physicalWidth) {
      this.canvas.width = physicalWidth;
    }
    if (this.canvas.height !== physicalHeight) {
      this.canvas.height = physicalHeight;
    }
  }

  installThemeSync() {
    if (globalThis.window?.addEventListener) {
      globalThis.window.addEventListener(THEME_EVENT, this.boundThemeSync);
    }
    if (globalThis.document?.documentElement && typeof MutationObserver !== "undefined") {
      this.themeObserver = new MutationObserver(() => this.syncTheme());
      this.themeObserver.observe(globalThis.document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
    }
  }

  teardownThemeSync() {
    if (globalThis.window?.removeEventListener) {
      globalThis.window.removeEventListener(THEME_EVENT, this.boundThemeSync);
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  }

  syncTheme() {
    const themePatch = buildThemePatch();
    if (themePatch) {
      this.setState(themePatch);
    }
  }

  #flushPendingStatePatch() {
    return this.measurePerformanceSpan("host.patch_flush", () => {
      if (
        !this.#runtimeInputDeliveryReady() ||
        !this.wasmModule?.fishymap_v2_apply_patch
      ) {
        return null;
      }
      if (!patchHasStateFields(this.pendingStatePatch)) {
        return null;
      }
      const patch = this.pendingStatePatch;
      this.pendingStatePatch = normalizeStatePatch({});
      const patches = statePatchToFishyMapV2Patches(patch);
      if (!patches.length) {
        return null;
      }
      const sequence = this.addV2InputSequence();
      const batch = createFishyMapV2InputPatchBatch(patches, {
        sequence,
        clientTimeMs: performanceNowMs(),
      });
      this.addPerformanceCounter("host.patches.flushed");
      this.addPerformanceCounter("host.v2.patch_batches.flushed");
      this.addPerformanceCounter("host.v2.input_patches.flushed", batch.patches.length);
      this.addPerformanceGauge("host.v2.input_batch_patch_count", batch.patches.length);
      this.setPerformanceLast("host.v2.input_batch_patch_count", batch.patches.length);
      recordFilterPerfMetrics(
        this.performanceCollector,
        "host.patch.filters",
        patch.filters,
      );
      for (const inputPatch of batch.patches) {
        this.observeHotBridgePayload(inputPatch, "input");
      }
      this.recordBridgePayload("egress", "input-patch-batch", batch);
      this.wasmModule.fishymap_v2_apply_patch(batch);
      return sequence;
    });
  }

  flushQueuedCommands() {
    this.measurePerformanceSpan("host.command_flush", () => {
      if (
        !this.#runtimeInputDeliveryReady() ||
        !this.wasmModule?.fishymap_v2_send_command
      ) {
        return;
      }
      const commands = this.pendingCommands.splice(0, this.pendingCommands.length);
      this.addPerformanceCounter("host.commands.flushed", commands.length);
      for (const command of commands) {
        const envelopes = commandsToFishyMapV2CommandEnvelopes(command, {
          commandIdPrefix: `host:${this.addV2CommandSequence()}`,
        });
        this.addPerformanceCounter("host.v2.command_envelopes.flushed", envelopes.length);
        for (const envelope of envelopes) {
          this.recordBridgePayload("egress", "command-envelope", envelope);
          this.wasmModule.fishymap_v2_send_command(envelope);
        }
      }
    });
  }

  refreshCurrentStateFromWasm() {
    return this.measurePerformanceSpan("host.state_pull", () => {
      if (!this.wasmReady || !this.wasmModule?.fishymap_v2_request_snapshot) {
        this.syncInputViewModeFromCurrentState();
        return this.currentState;
      }
      this.addPerformanceCounter("host.wasm.state_reads");
      const response = this.wasmModule.fishymap_v2_request_snapshot({
        version: FISHYMAP_BRIDGE_V2_VERSION,
        kind: "state",
      });
      this.applySnapshotResponse(response);
      this.syncInputViewModeFromCurrentState();
      return this.currentState;
    });
  }

  scheduleDeferredStateRefresh(kind = "state") {
    if (!this.wasmReady || !this.wasmModule?.fishymap_v2_request_snapshot) {
      return;
    }
    const normalizedKind = kind === "bootstrap" ? "bootstrap" : "state";
    if (normalizedKind === "state" || !this.deferredStateRefreshKind) {
      this.deferredStateRefreshKind = normalizedKind;
    }
    this.addPerformanceCounter("host.v2.state_refresh_deferred");
    this.schedulePostSinkDrain();
  }

  flushDeferredStateRefresh() {
    this.measurePerformanceSpan("host.deferred_state_pull", () => {
      if (!this.wasmReady || !this.wasmModule?.fishymap_v2_request_snapshot) {
        return;
      }
      const refreshKind = this.deferredStateRefreshKind || "state";
      this.deferredStateRefreshKind = "";
      if (refreshKind === "bootstrap") {
        this.refreshBootstrapStateFromWasm();
      } else {
        this.refreshCurrentStateFromWasm();
      }
      this.addPerformanceCounter("host.v2.state_refresh_deferred_flushed");
      this.addPerformanceCounter(`host.v2.state_refresh_deferred_flushed.${refreshKind}`);
      const stateDelta = {
        type: "state-delta",
        version: this.currentState.version || FISHYMAP_CONTRACT_VERSION,
        snapshotKind: refreshKind,
      };
      if (refreshKind === "bootstrap") {
        stateDelta.ready = this.currentState.ready === true;
        stateDelta.statuses = cloneJson(this.currentState.statuses || {});
      }
      this.queueShellV2Events([
        stateDelta,
      ]);
    });
  }

  refreshBootstrapStateFromWasm() {
    return this.measurePerformanceSpan("host.bootstrap_pull", () => {
      if (!this.wasmReady) {
        this.syncInputViewModeFromCurrentState();
        return this.currentState;
      }
      if (!this.wasmModule?.fishymap_v2_request_snapshot) {
        return this.refreshCurrentStateFromWasm();
      }
      this.addPerformanceCounter("host.wasm.bootstrap_reads");
      const response = this.wasmModule.fishymap_v2_request_snapshot({
        version: FISHYMAP_BRIDGE_V2_VERSION,
        kind: "bootstrap",
      });
      this.applySnapshotResponse(response);
      this.syncInputViewModeFromCurrentState();
      return this.currentState;
    });
  }

  applySnapshotResponse(response) {
    if (!isPlainObject(response)) {
      throw new TypeError("map snapshot response must be an object");
    }
    if (response.version !== FISHYMAP_BRIDGE_V2_VERSION) {
      throw new TypeError(
        `unsupported map snapshot response version: ${String(response.version)}`,
      );
    }
    const kind = normalizeFishyMapV2SnapshotKind(response.kind);
    if (response.deferred === true) {
      this.addPerformanceCounter("host.v2.snapshots_deferred");
      return false;
    }
    return this.applySnapshotPayload(kind, response.snapshot);
  }

  applySnapshotPayload(kind, snapshot) {
    const normalizedKind = normalizeFishyMapV2SnapshotKind(kind);
    const projection = projectFishyMapSnapshotPayload(
      this.currentState,
      normalizedKind,
      snapshot,
    );
    if (projection.applied) {
      this.currentState = projection.currentState;
      this.syncInputViewModeFromCurrentState();
      this.addPerformanceCounter(`host.v2.snapshots_applied.${normalizedKind}`);
      return true;
    }
    this.addPerformanceCounter(`host.v2.snapshots_applied.${normalizePerfCounterSuffix(normalizedKind)}`);
    return false;
  }

  syncInputViewModeFromCurrentState() {
    if (this.inputState?.ui?.threeDEnabled === false) {
      this.inputState.ui.viewMode = "2d";
      return;
    }
    if (!this.inputState?.ui || this.inputState.ui.viewMode != null) {
      return;
    }
    this.inputState.ui.viewMode = resolvedCurrentViewMode(this.currentState);
  }

  #runtimeInputDeliveryReady() {
    return this.wasmReady && this.runtimeInputReady && !this.runtimeInputReleasePending;
  }

  #markRuntimeInputReady() {
    if (this.runtimeInputReady) {
      return;
    }
    this.runtimeInputReady = true;
    this.runtimeInputReleasePending = true;
    this.addPerformanceCounter("host.v2.runtime_input_ready");
    this.schedulePostSinkDrain();
  }

  handleWasmV2EventBatch(batch) {
    this.measurePerformanceSpan("host.handle_v2_event_batch", () => {
      const events = assertFishyMapV2EventBatch(batch);
      const incomingAppliedInputSequence = batch.appliedInputSequence ?? null;
      const inputSequenceUpdate = applyV2InputAcknowledgement(
        this.appliedInputSequence,
        this.v2InputSequence,
        incomingAppliedInputSequence,
      );
      if (inputSequenceUpdate.advanced) {
        this.appliedInputSequence = inputSequenceUpdate.appliedInputSequence;
        this.addPerformanceCounter("host.v2.input_sequences_applied");
        this.setPerformanceLast(
          "host.v2.applied_input_sequence",
          inputSequenceUpdate.appliedInputSequence,
        );
      }
      const revisionUpdate = applyV2StateRevisions(
        this.stateRevisions,
        batch.stateRevisions,
      );
      this.stateRevisions = revisionUpdate.revisions;
      if (!events.length && !revisionUpdate.changed.length && !inputSequenceUpdate.advanced) {
        this.#markRuntimeInputReady();
        return;
      }
      this.addPerformanceCounter("host.v2.event_batches");
      this.addPerformanceGauge("host.v2.event_batch_size", events.length);
      this.setPerformanceLast("host.v2.event_batch_size", events.length);
      for (const key of revisionUpdate.changed) {
        this.addPerformanceCounter(`host.v2.state_revisions.${key}`);
      }
      this.recordBridgePayload("ingress", "wasm-v2-event-batch", {
        ...batch,
        stateRevisions: revisionUpdate.revisions,
        events,
      });
      for (const entry of events) {
        this.observeHotBridgePayload(entry, "wasm");
      }
      if (revisionUpdate.changed.includes("catalogRev")) {
        this.scheduleDeferredStateRefresh("state");
      } else if (revisionUpdate.changed.includes("statusRev")) {
        this.scheduleDeferredStateRefresh("bootstrap");
      }
      for (const entry of events) {
        this.handleWasmEventPayload(entry);
      }
      if (events.length || inputSequenceUpdate.advanced) {
        this.queueShellV2Events(events, {
          appliedInputSequence: inputSequenceUpdate.advanced
            ? inputSequenceUpdate.appliedInputSequence
            : 0,
        });
      }
      this.#markRuntimeInputReady();
    });
  }

  handleWasmEventPayload(payload) {
    if (!isPlainObject(payload)) {
      throw new TypeError("FishyMap output event must be an object");
    }

    const { type } = payload;
    const suffix = normalizePerfCounterSuffix(type) || "unknown";
    this.addPerformanceCounter(`host.events.handled.${suffix}`);
    if (type === "snapshot-ready") {
      this.addPerformanceCounter("host.v2.snapshot_ready_events");
      this.applySnapshotPayload(payload.kind, payload.snapshot);
      return;
    }

    if (type === "first-frame") {
      this.markFirstFrameReady();
      return;
    }

    const projection = projectFishyMapStateEvent(
      this.currentState,
      this.inputState,
      payload,
    );
    this.currentState = projection.currentState;
    this.inputState = projection.inputState;
  }

}

export function createFishyMapBridge() {
  return new FishyMapBridgeImpl();
}

const FishyMapBridge = createFishyMapBridge();

if (typeof window !== "undefined") {
  window.FishyMapBridge = FishyMapBridge;
}

export default FishyMapBridge;
